import { For, Show } from 'solid-js'
import grassFgImg from '~/assets/figma/grass-fg.webp'
import heroBgImg from '~/assets/figma/hero-bg.webp'
import { m } from '~/paraglide/messages'
import { openRulesDialog } from '~/store/rules-dialog'

type SparkleShape = 'star' | 'circle'

type Sparkle = {
  shape: SparkleShape
  x: number
  y: number
  s: number
  d: number
}

const SPARKLES: Sparkle[] = [
  { shape: 'star', x: -135, y: 90, s: 16, d: 0.0 },
  { shape: 'circle', x: -75, y: 110, s: 8, d: 1.4 },
  { shape: 'circle', x: -100, y: 70, s: 5, d: 1.9 },
  { shape: 'circle', x: -160, y: 95, s: 6, d: 2.3 },
  { shape: 'circle', x: -50, y: 80, s: 5, d: 0.5 },
  { shape: 'circle', x: -165, y: 130, s: 7, d: 0.4 },
  { shape: 'circle', x: -110, y: 135, s: 7, d: 0.6 },
  { shape: 'circle', x: -55, y: 155, s: 6, d: 1.1 },
  { shape: 'circle', x: -180, y: 165, s: 5, d: 0.8 },
  { shape: 'star', x: -165, y: 140, s: 22, d: 1.2 },
  { shape: 'circle', x: -130, y: 190, s: 6, d: 0.1 },
  { shape: 'circle', x: -90, y: 195, s: 6, d: 0.3 },
  { shape: 'circle', x: -150, y: 230, s: 7, d: 1.5 },
  { shape: 'star', x: -115, y: 220, s: 16, d: 1.6 },
  { shape: 'circle', x: -95, y: 260, s: 7, d: 2.4 },
  { shape: 'circle', x: -75, y: 285, s: 7, d: 2.0 },
  { shape: 'circle', x: -10, y: 235, s: 6, d: 1.7 },
  { shape: 'circle', x: 35, y: 250, s: 7, d: 0.9 },
  { shape: 'circle', x: 80, y: 230, s: 5, d: 2.1 },
  { shape: 'circle', x: 25, y: 285, s: 6, d: 1.3 },
  { shape: 'circle', x: 70, y: 280, s: 7, d: 0.4 },
  { shape: 'circle', x: -25, y: 295, s: 6, d: 2.5 },
]

export function HeroSection(props: { started?: boolean }) {
  const introStyle = (delayMs: number) =>
    props.started
      ? {
          animation: 'introRise 0.6s ease-out both',
          'animation-delay': `${delayMs}ms`,
        }
      : undefined

  const openRules = () => {
    openRulesDialog()
  }

  return (
    <div class="relative h-[200px] w-full shrink-0">
      <div class="absolute left-[calc(50vw-40px)] top-1/2 flex w-[clamp(200px,17vw,280px)] -translate-y-1/2 flex-col gap-2 [filter:drop-shadow(0_0_4px_rgba(255,255,255,0.85))]">
        <h1
          class="w-full whitespace-pre-line bg-clip-text py-2 font-display text-[clamp(36px,3.2vw,44px)] leading-[1.07] text-transparent opacity-0 [background-image:radial-gradient(119.07%_75.47%_at_60.47%_66.82%,#CC8033_2.71%,#66401A_100%)]"
          style={introStyle(300)}
        >
          {m.bakery_home_hero_title()}
        </h1>
        <div
          class="flex w-full flex-col gap-2 opacity-0"
          style={introStyle(600)}
        >
          <p class="w-full text-[14px] leading-[20px] text-bakery-brown-mid opacity-85">
            {m.bakery_home_hero_text()}
          </p>
          <div class="flex w-full justify-start">
            <button
              type="button"
              onClick={openRules}
              class="inline-flex items-center gap-1 text-xs leading-4 text-bakery-pink transition-opacity active:opacity-70"
            >
              {m.event_rule_title()}
              <svg
                viewBox="0 0 12 12"
                class="size-3"
                fill="none"
                stroke="currentColor"
                stroke-width="1.03"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <path d="M4.455 9.96L7.715 6.7c.385-.385.385-1.015 0-1.4L4.455 2.04" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export function HeroBackground(props: { hideSparkles?: boolean }) {
  return (
    <>
      <div class="pointer-events-none absolute bottom-[67px] left-[-183px] flex size-[423px] rotate-180 -scale-y-100 items-center justify-center">
        <div class="size-[407px] opacity-30 blur-[2px]">
          <div class="flex size-full items-center justify-center">
            <div class="-rotate-[14.37deg]">
              <img
                src={grassFgImg}
                alt=""
                aria-hidden="true"
                class="h-[330px] w-[352px] max-w-none select-none object-bottom blur-[3.07px]"
              />
            </div>
          </div>
        </div>
      </div>

      <img
        src={heroBgImg}
        alt=""
        aria-hidden="true"
        class="pointer-events-none absolute left-1/2 top-0 h-[500px] w-[1194px] max-w-none -translate-x-1/2 select-none object-cover [-webkit-mask-image:linear-gradient(to_bottom,black_72%,transparent_100%)] [mask-image:linear-gradient(to_bottom,black_72%,transparent_100%)]"
      />

      <Show when={!props.hideSparkles}>
        <HeroSparkles />
      </Show>

      <div class="pointer-events-none absolute right-[-184px] top-[282px] flex h-[345px] w-[358px] items-center justify-center">
        <div class="-rotate-[14.37deg]">
          <img
            src={grassFgImg}
            alt=""
            aria-hidden="true"
            class="h-[279px] w-[298px] max-w-none select-none object-bottom blur-[2.6px]"
          />
        </div>
      </div>
    </>
  )
}

function HeroSparkles() {
  return (
    <div class="pointer-events-none absolute left-1/2 top-[30px] z-10 size-px -translate-x-1/2">
      <For each={SPARKLES}>
        {(sp) => (
          <svg
            viewBox="0 0 24 24"
            aria-hidden="true"
            class="absolute animate-sparkle"
            style={{
              left: `${sp.x}px`,
              top: `${sp.y}px`,
              width: `${sp.s}px`,
              height: `${sp.s}px`,
              transform: 'translate(-50%, -50%)',
              'animation-delay': `${sp.d}s`,
              opacity: 0,
              filter: 'drop-shadow(0 0 4px rgba(255,255,180,0.9))',
            }}
          >
            <Show
              when={sp.shape === 'star'}
              fallback={<circle cx="12" cy="12" r="6" fill="white" />}
            >
              <path
                d="M12 0 L13.5 10.5 L24 12 L13.5 13.5 L12 24 L10.5 13.5 L0 12 L10.5 10.5 Z"
                fill="white"
              />
            </Show>
          </svg>
        )}
      </For>
    </div>
  )
}
